/**
 * VE-31538 — refusing a set-wide write while somebody else holds the set.
 *
 * WHAT THIS GUARDS, and why it is not covered by the editor going read-only.
 * `mergedSetBlock` turns the open document's editor view-only, which stops
 * the buttons in the editor. It does not stop anything else: membership
 * changes and deletes are also issued from the FOLDER VIEW, where no editor
 * session exists and that field is null. These operations rewrite the whole
 * set — a rebuild reads every member, a membership write replaces the member
 * list outright — so running one while a colleague is mid-edit in a member
 * discards work that was never on screen.
 *
 * ONE REASON, STATED ONCE. The ticket's §4 asked for this: a set-wide refusal
 * is one fact about the set, not N anonymous per-file failures in a summary.
 * The gate names the holder.
 *
 * IT FAILS CLOSED. An unreadable lock refuses the write, which is the
 * opposite of what the heartbeat does with the same `undefined` — the
 * heartbeat can afford to wait a tick and ask again, while a write has no
 * later chance to be right. Refusing costs a retry; proceeding costs a
 * colleague's redactions.
 */
import { put, putResolve, select } from 'typed-redux-saga/macro';

import { enqueueSnackbar } from '@common-modules/snackbar';
import { sagaIntl } from '@i18n';
import { selectUser } from '@helpers/tdoHelper';
import { selectFeatureFlags } from '@common/user-permissions';

import { serviceReadProjectSetLock, type SetLockVerdict } from './setLock';

/**
 * May this set-wide write proceed?
 *
 * Returns false having already told the reviewer why, so callers read as
 * `if (!(yield* ensureSetUnlocked(id))) return;`.
 */
export function* ensureSetUnlocked(projectTdoId: string | undefined) {
  if (!projectTdoId) {
    // Nothing to be locked. A request with no merged document reaches none of
    // these paths, but a caller that cannot name the project has not
    // established the set is free either — it has established there is no
    // set-wide write to make.
    return true;
  }

  // Nothing to enforce where locking is switched off. Without this the gate
  // would refuse every rebuild, membership change and Bates write in an org
  // that does not use locks at all, since the read it depends on can never
  // come back positive there.
  const flags = yield* select(selectFeatureFlags);
  if (!flags?.tdoLock) {
    return true;
  }

  const intl = sagaIntl();
  const user = yield* select(selectUser);
  const userId = user?.userId;

  // `putResolve`, the way `purgeRedactStateSdos` reads its thunks: this needs
  // the service's RETURN value, and the thunk needs the real store's
  // dispatch and getState to resolve the schema id and the API config.
  //
  // NO USER IS NOT AN ANSWER. Without one we cannot tell our own lock from a
  // colleague's — every record looks foreign, or none does, depending on
  // which way the comparison falls — so the read is not worth making and its
  // result would mean nothing. This used to return true, which made an
  // unloaded user slice the easiest way to walk straight through the gate.
  // Left unset, it falls through to the same refusal a failed read gets.
  let verdict: SetLockVerdict | undefined;
  if (userId) {
    try {
      verdict = (yield* putResolve(
        serviceReadProjectSetLock({ projectTdoId, userId }) as never
      )) as SetLockVerdict | undefined;
    } catch {
      // A throw is the same answer as a failed read: we do not know, so we
      // refuse. Falls through to the message below with `verdict` unset.
      verdict = undefined;
    }
  }

  // `unavailable` — no lock registry configured on this cluster — is as good
  // as free: there is no record anyone could be holding. An empty answer from
  // a registry that IS configured does not reach here as `unavailable`; the
  // service reports that as a failed read (see `resolveLockSchema`).
  if (verdict && (verdict.unavailable || !verdict.isHeldByOther)) {
    return true;
  }

  yield* put(
    enqueueSnackbar({
      message: verdict?.isHeldByOther
        ? intl.formatMessage(
            {
              id: 'mergedDocumentSetLockedForWrite',
              defaultMessage:
                'This merged document is currently being edited by {name}. Try again once they are finished.',
            },
            {
              name:
                verdict.heldBy ||
                intl.formatMessage({
                  id: 'anotherUser',
                  defaultMessage: 'another user',
                }),
            }
          )
        : intl.formatMessage({
            id: 'mergedDocumentSetLockUnknown',
            defaultMessage:
              'Could not check whether the merged document is in use. Please try again.',
          }),
      variant: 'warning',
    })
  );
  return false;
}
